import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";
import CtaBand from "@/components/feature/CtaBand";
import { useSeo } from "@/hooks/useSeo";
import { brand, principles, stats } from "@/mocks/site";
import { testimonials } from "@/mocks/testimonials";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于爆点投稿社",
    description:
      "爆点投稿社是一个爆料投稿与选题参考平台，为投稿者提供高热度选题、爆款稿件特征与编辑偏好方向。",
    url: "https://sxnxp.cn/about",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brand.name,
    url: "https://sxnxp.cn/",
    email: brand.email,
    foundingDate: brand.founded,
    description: brand.intro,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://sxnxp.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sxnxp.cn/about" },
    ],
  },
];

const toneStyles: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function About() {
  useSeo({
    title: "关于我们 - 爆点投稿社的品牌定位与内容原则",
    description:
      "了解爆点投稿社的品牌定位、发展理念与内容原则。我们把投稿标准透明化，帮助普通人写出能被采用的爆料投稿线索。",
    keywords: "关于爆点投稿社,投稿平台,内容原则,爆料投稿,编辑偏好方向",
    path: "/about",
    jsonLd,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
        <div className="mx-auto w-full">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              关于我们
            </p>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              把投稿这件事，讲得再清楚一点
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              爆点投稿社成立于 {brand.founded} 年，起初只是几个编辑整理的一份「什么选题容易过」的内部清单。
              后来我们发现，投稿难往往不是因为线索没价值，而是因为写的人不知道编辑在看什么。
              于是这份清单被公开出来，慢慢长成了一个面向所有人的投稿选题参考平台。
            </p>
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 rounded-lg border border-background-200 bg-background-50 p-6 lg:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label}>
                <dt className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {item.value}
                </dt>
                <dd className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto grid w-full grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20photo%20of%20an%20open%20notebook%20with%20handwritten%20topic%20lists%20beside%20a%20cup%20of%20tea%20and%20dried%20flowers%2C%20soft%20window%20light%2C%20muted%20cream%20and%20sage%20tones%2C%20calm%20minimal%20magazine%20composition%2C%20high%20detail&width=1100&height=900&seq=baodian-about-editorial&orientation=landscape"
                alt="写满选题清单的笔记本与茶，代表爆点投稿社的编辑工作"
                title="关于爆点投稿社 - 选题清单与编辑理念"
                className="h-[320px] w-full object-cover object-top md:h-[420px]"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="平台定位"
              title="不做匿名爆料墙，做一份看得懂的投稿说明书"
              desc="我们相信，一次好的爆料，主角应该是事实本身，而不是情绪。平台做的所有事情，都围绕一个目标：让真实、有公共价值的线索，更容易被看见。"
            />
            <div className="mt-8 space-y-5">
              <div className="flex gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-focus-3-line text-lg leading-none"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">写给没有经验的投稿者</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    不使用任何行业内行话。选题怎么看、材料怎么准备、标题怎么改，都拆成可以照着做的步骤。
                  </p>
                </div>
              </div>
              <div className="flex gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-shield-star-line text-lg leading-none"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">把投稿者的安全放在第一位</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    匿名与化名都是常规选项。涉及敏感内容时，我们会先与你确认保护方案，再决定是否推进选题。
                  </p>
                </div>
              </div>
              <div className="flex gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-eye-line text-lg leading-none"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">标准透明，可被对照检查</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    编辑判断稿件的维度全部公开。你可以用同一把尺子衡量自己的稿件，也能理解为什么被退回。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto w-full">
          <SectionHeading
            align="center"
            eyebrow="内容原则"
            title="这四条原则，我们从不妥协"
            desc="它们决定了哪些线索我们会跟进，哪些材料即使热度再高也不会采用。"
          />
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 90}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-2xl leading-none`}></i>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto w-full">
          <SectionHeading
            eyebrow="投稿者评价"
            title="被编辑回访过的他们，这样说"
            desc="这些话来自通过平台提交过线索、并进入跟进流程的投稿者。"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.slice(0, 3).map((item) => (
              <figure
                key={item.name}
                className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100/60 p-6"
              >
                <i className="ri-double-quotes-l text-2xl leading-none text-primary-300"></i>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.content}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-full font-heading text-sm font-bold ${toneStyles[item.tone]}`}
                  >
                    {item.initial}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-mail-send-line text-xl leading-none"></i>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">投稿与投稿咨询</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              投稿邮箱：{brand.email}
              <br />
              也可通过在线投稿表单提交，支持匿名与化名。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-time-line text-xl leading-none"></i>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">答复时间</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              {brand.replyPolicy}。节假日顺延，需要补充材料的选题会单独约定时间。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-customer-service-2-line text-xl leading-none"></i>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">合作与媒体转载</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              媒体合作、线索共建请在邮件标题注明「合作」，我们会转交对应编辑对接。
            </p>
          </div>
        </div>

        <div className="mx-auto mt-8 w-full">
          <Link
            to="/submit"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
          >
            去投稿页提交线索
            <i className="ri-arrow-right-line text-base leading-none"></i>
          </Link>
        </div>
      </section>

      <CtaBand
        title="了解了我们，就把线索交给我们吧"
        desc="不需要文采，也不用等材料完美。先把你知道的事情讲清楚，剩下的一起完成。"
        secondaryLabel="看看正在征集什么"
        secondaryTo="/topics"
      />
    </>
  );
}